import { reactive } from 'vue'
import { useLogStore } from '~~/stores/log'

type EventHandler = (event: unknown) => void

interface EventsState {
    connected: boolean
    connectionId: string | null
    retryDelay: number
    countdownSeconds: number
    countdownInterval: ReturnType<typeof setInterval> | null
    retryTimeout: ReturnType<typeof setTimeout> | null
    source: EventSource | null
    handlers: Map<string, Set<EventHandler>>
}

const MIN_DELAY = 1
const MAX_DELAY = 60

const state = reactive<EventsState>({
    connected: false,
    connectionId: null,
    retryDelay: MIN_DELAY,
    countdownSeconds: 0,
    countdownInterval: null,
    retryTimeout: null,
    source: null,
    handlers: new Map(),
})

function connect() {
    if (state.source) {
        state.source.close()
        state.source = null
    }
    if (state.retryTimeout) {
        clearTimeout(state.retryTimeout)
        state.retryTimeout = null
    }
    if (state.countdownInterval) {
        clearInterval(state.countdownInterval)
        state.countdownInterval = null
    }

    const source = new EventSource('/api/me/events')
    state.source = source

    source.onopen = () => {
        state.connected = true
        state.retryDelay = MIN_DELAY
        state.countdownSeconds = 0
        const logStore = useLogStore()
        logStore.info('SSE reconnected')
    }

    source.onerror = () => {
        state.connected = false
        source.close()
        state.source = null
        const logStore = useLogStore()
        logStore.error('SSE connection failed')
        scheduleReconnect()
    }

    source.addEventListener('message', (evt) => {
        try {
            const data = JSON.parse(evt.data)
            const type = data.type as string
            if (type === 'connected' && typeof data.connectionId === 'string') {
                state.connectionId = data.connectionId
            }
            const handlers = state.handlers.get(type)
            if (handlers) {
                for (const handler of handlers) {
                    handler(data)
                }
            }
        }
        catch { /* ignore malformed events */ }
    })

    // Generic SSE events (custom event types from the server)
    // The server sends events with custom `event` field; EventSource fires them separately.
    // We'll wire them through the `message` path via data.type, so no extra listener needed.
}

function scheduleReconnect() {
    const delay = state.retryDelay
    state.countdownSeconds = delay
    state.retryDelay = Math.min(state.retryDelay * 2, MAX_DELAY)

    state.countdownInterval = setInterval(() => {
        state.countdownSeconds--
        if (state.countdownSeconds <= 0) {
            clearInterval(state.countdownInterval!)
            state.countdownInterval = null
        }
    }, 1000)

    state.retryTimeout = setTimeout(() => {
        connect()
    }, delay * 1000)
}

export function useEvents() {
    return {
        connect,

        reconnectNow() {
            state.retryDelay = MIN_DELAY
            connect()
        },

        on(eventType: string, handler: EventHandler): () => void {
            let handlers = state.handlers.get(eventType)
            if (!handlers) {
                handlers = new Set()
                state.handlers.set(eventType, handlers)
            }
            handlers.add(handler)
            return () => {
                handlers!.delete(handler)
                if (handlers!.size === 0) {
                    state.handlers.delete(eventType)
                }
            }
        },

        get connected() {
            return state.connected
        },

        get connectionId() {
            return state.connectionId
        },

        get countdownSeconds() {
            return state.countdownSeconds
        },
    }
}
